// ============================================================
// Assessment Taking Page - Candidate-facing timed assessment
// ============================================================

import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, ChevronLeft, ChevronRight, Send, AlertTriangle, Play, CheckCircle, Video } from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function AssessmentTakingPage() {
    const { token } = useParams();
    const queryClient = useQueryClient();
    const [started, setStarted] = useState(false);
    const [attemptId, setAttemptId] = useState<string | null>(null);
    const [answers, setAnswers] = useState<Record<string, any>>({});
    const [submitted, setSubmitted] = useState(false);
    const [candidateName, setCandidateName] = useState('');
    const [candidateEmail, setCandidateEmail] = useState('');

    // Section Taker State
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const [sectionQuestions, setSectionQuestions] = useState<any[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [sectionTimeLeft, setSectionTimeLeft] = useState<number | null>(null);
    const sectionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Fetch assessment details and invite state
    const { data: info, isLoading, error } = useQuery({
        queryKey: ['assessment-info', token],
        queryFn: async () => {
            const res = await fetch(`${API_BASE}/public/assessment/${token}`);
            if (!res.ok) {
                const d = await res.json();
                throw new Error(d.error || 'Assessment not found');
            }
            return res.json();
        },
    });

    const assessInfo = info?.data;

    // Auto-restore attempt if active
    useEffect(() => {
        if (assessInfo && assessInfo.activeAttempt && !started) {
            const d = assessInfo.activeAttempt;
            setAttemptId(d.attemptId);
            setStarted(true);
            
            if (assessInfo.candidateName) setCandidateName(assessInfo.candidateName);
            if (assessInfo.email) setCandidateEmail(assessInfo.email);

            // Load saved responses
            if (d.savedResponses) {
                const saved: Record<string, any> = {};
                d.savedResponses.forEach((r: any) => { saved[r.questionVersionId] = r.answer; });
                setAnswers(saved);
            }

            // If mid-section, we should auto-load that section!
            if (d.currentSectionId) {
                setTimeout(() => {
                    resumeSection(d.currentSectionId, d.attemptId);
                }, 100);
            }
        }
    }, [assessInfo]);

    // Start assessment attempt
    const startMutation = useMutation({
        mutationFn: async () => {
            const res = await fetch(`${API_BASE}/public/assessment/${token}/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: candidateName, email: candidateEmail }),
            });
            if (!res.ok) {
                const d = await res.json();
                throw new Error(d.error || 'Failed to start');
            }
            return res.json();
        },
        onSuccess: (data) => {
            const d = data.data;
            setAttemptId(d.attemptId);
            setStarted(true);

            // Load saved responses
            if (d.savedResponses) {
                const saved: Record<string, any> = {};
                d.savedResponses.forEach((r: any) => { saved[r.questionVersionId] = r.answer; });
                setAnswers(saved);
            }

            // Sync active attempt metadata
            if (d.currentSectionId) {
                resumeSection(d.currentSectionId, d.attemptId);
            }
        },
        onError: (err: any) => toast.error(err.message),
    });

    // Start/Load Section
    const startSectionMutation = useMutation({
        mutationFn: async ({ sectionId, attId }: { sectionId: string; attId: string }) => {
            const res = await fetch(`${API_BASE}/public/assessment/${token}/section/${sectionId}/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attemptId: attId }),
            });
            if (!res.ok) {
                const d = await res.json();
                throw new Error(d.error || 'Failed to start section');
            }
            return res.json();
        },
        onSuccess: (data, variables) => {
            const d = data.data;
            setSectionQuestions(d.questions);
            setCurrentQuestionIndex(0);
            setSectionTimeLeft(d.timeLeftSeconds);
            setActiveSectionId(variables.sectionId);
            queryClient.invalidateQueries({ queryKey: ['assessment-info', token] });
        },
        onError: (err: any) => toast.error(err.message),
    });

    // Auto-save Question Answer
    const saveMutation = useMutation({
        mutationFn: async ({ questionVersionId, answer }: { questionVersionId: string; answer: any }) => {
            const currentAttId = attemptId || assessInfo?.activeAttempt?.attemptId;
            if (!currentAttId) return;
            await fetch(`${API_BASE}/public/assessment/${token}/save`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attemptId: currentAttId, questionVersionId, answer }),
            });
        },
    });

    // Submit Section
    const submitSectionMutation = useMutation({
        mutationFn: async ({ sectionId, attId }: { sectionId: string; attId: string }) => {
            const res = await fetch(`${API_BASE}/public/assessment/${token}/section/${sectionId}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attemptId: attId }),
            });
            if (!res.ok) throw new Error('Failed to submit section');
            return res.json();
        },
        onSuccess: (data, variables) => {
            const d = data.data;
            if (sectionTimerRef.current) clearInterval(sectionTimerRef.current);
            setSectionQuestions([]);
            setActiveSectionId(null);
            setSectionTimeLeft(null);

            // Invalidate queries so dashboard statuses reload
            queryClient.invalidateQueries({ queryKey: ['assessment-info', token] });
            
            const currentIdx = assessInfo?.sections?.findIndex((s: any) => s.id === variables.sectionId);
            const nextSection = assessInfo?.sections?.[currentIdx + 1];

            if (d.allCompleted) {
                // If all sections completed, submit assessment
                submitAssessmentMutation.mutate({ attId: attemptId || variables.attId, autoSubmitted: false });
            } else if (nextSection) {
                toast.success(`Section completed! Auto-advancing to: ${nextSection.name}...`);
                resumeSection(nextSection.id, attemptId || variables.attId);
            } else {
                toast.success('Section completed! Please proceed to the next section.');
            }
        },
        onError: (err: any) => toast.error(err.message),
    });

    // Submit Entire Assessment
    const submitAssessmentMutation = useMutation({
        mutationFn: async ({ attId, autoSubmitted }: { attId: string; autoSubmitted: boolean }) => {
            const res = await fetch(`${API_BASE}/public/assessment/${token}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attemptId: attId, autoSubmitted }),
            });
            if (!res.ok) throw new Error('Submission failed');
            return res.json();
        },
        onSuccess: () => {
            setSubmitted(true);
            if (sectionTimerRef.current) clearInterval(sectionTimerRef.current);
        },
    });

    const resumeSection = (secId: string, attId: string) => {
        startSectionMutation.mutate({ sectionId: secId, attId });
    };

    // Section Timer Effect
    useEffect(() => {
        if (!activeSectionId || sectionTimeLeft === null || submitted) return;

        sectionTimerRef.current = setInterval(() => {
            setSectionTimeLeft(prev => {
                if (prev === null) return null;
                if (prev <= 1) {
                    clearInterval(sectionTimerRef.current!);
                    toast.error("Time limit reached! Section automatically submitted.");
                    submitSectionMutation.mutate({ sectionId: activeSectionId, attId: attemptId! });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => { if (sectionTimerRef.current) clearInterval(sectionTimerRef.current); };
    }, [activeSectionId, sectionTimeLeft, submitted, attemptId]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const handleAnswer = useCallback((qvId: string, answer: any) => {
        setAnswers(prev => ({ ...prev, [qvId]: answer }));
        saveMutation.mutate({ questionVersionId: qvId, answer });
    }, [attemptId]);

    if (isLoading) return <div className="loading-page"><div className="loading-spinner" /></div>;
    if (error) return (
        <div className="loading-page">
            <div style={{ textAlign: 'center' }}>
                <AlertTriangle size={48} color="#f59e0b" style={{ marginBottom: 16 }} />
                <h2 style={{ marginBottom: 8 }}>Assessment Unavailable</h2>
                <p style={{ color: '#6b7280' }}>{(error as Error).message}</p>
            </div>
        </div>
    );

    if (submitted) return (
        <div className="loading-page animate-fadeIn">
            <div style={{ textAlign: 'center', padding: '40px 24px' }}>
                <div style={{ fontSize: '4rem', marginBottom: 16, animation: 'bounce 1.5s infinite' }}>🎉</div>
                <h2 style={{ marginBottom: 12, fontWeight: 800, fontSize: '1.8rem' }}>Assessment Submitted!</h2>
                <p style={{ color: '#4b5563', maxWidth: 460, margin: '0 auto', fontSize: '14px', lineHeight: 1.6 }}>
                    Your multi-section assessment is complete and your responses have been recorded. You can safely close this browser window.
                </p>
            </div>
        </div>
    );

    // Pre-start screen
    if (!started) return (
        <div className="loading-page">
            <div className="auth-card animate-slideUp" style={{ maxWidth: 520, padding: 32 }}>
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{
                        width: 60, height: 60, fontSize: 32,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        borderRadius: 16, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 800, marginBottom: 16,
                        boxShadow: '0 8px 16px rgba(99, 102, 241, 0.2)',
                    }}>📝</div>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 4 }}>{assessInfo?.assessmentName}</h1>
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>Welcome, {assessInfo?.candidateName || 'Candidate'}</p>
                </div>

                <div style={{ background: 'var(--color-bg-tertiary)', borderRadius: 12, padding: 20, marginBottom: 24, border: '1px solid var(--color-border)' }}>
                    <h4 style={{ fontWeight: 700, fontSize: '13px', marginBottom: 12, color: 'var(--color-text-secondary)' }}>ASSESSMENT STRUCTURE:</h4>
                    {assessInfo?.sections?.map((s: any, idx: number) => (
                        <div key={s.id} className="flex justify-between items-center mb-2" style={{ fontSize: '13px' }}>
                            <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{idx + 1}. {s.name} ({s.type})</span>
                            <span style={{ color: '#6b7280' }}>
                                {s.questionCount} Qs · {s.timeLimitMinutes ? `${s.timeLimitMinutes}m` : 'No limit'}
                            </span>
                        </div>
                    ))}
                </div>

                <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.15)', borderRadius: 12, padding: 16, marginBottom: 24, fontSize: '13px', color: '#f59e0b', lineHeight: 1.5 }}>
                    ⚠️ Once you start, you will complete the tests section-by-section. Timers run per-section and cannot be paused.
                </div>

                {/* Identity capture */}
                <div style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>Your Full Name *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={candidateName}
                            onChange={e => setCandidateName(e.target.value)}
                            placeholder="e.g. John Doe"
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>Your Email Address *</label>
                        <input
                            type="email"
                            className="form-input"
                            value={candidateEmail}
                            onChange={e => setCandidateEmail(e.target.value)}
                            placeholder="e.g. john@example.com"
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>

                <button className="btn btn-primary btn-lg w-full" onClick={() => {
                    if (!candidateName.trim() || !candidateEmail.trim()) {
                        toast.error('Please enter your name and email');
                        return;
                    }
                    startMutation.mutate();
                }}
                    disabled={startMutation.isPending}>
                    {startMutation.isPending ? 'Starting...' : 'Start Assessment'}
                </button>
            </div>
        </div>
    );

    // Section Taker UI (if section is active)
    if (activeSectionId && sectionQuestions.length > 0) {
        const currentQ = sectionQuestions[currentQuestionIndex];
        return (
            <div style={{ minHeight: '100vh', background: 'var(--color-bg-primary)', padding: 24 }}>
                {/* Top Bar */}
                <div className="flex justify-between items-center mb-6" style={{
                    position: 'sticky', top: 0, background: 'var(--color-bg-primary)', paddingBottom: 16, zIndex: 50,
                    borderBottom: '1px solid var(--color-border)',
                }}>
                    <div>
                        <h2 style={{ fontSize: '15px', fontWeight: 700 }}>
                            {assessInfo?.sections?.find((s: any) => s.id === activeSectionId)?.name}
                        </h2>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                            Question {currentQuestionIndex + 1} of {sectionQuestions.length}
                        </span>
                    </div>

                    {sectionTimeLeft !== null && (
                        <div className={`timer ${sectionTimeLeft < 180 ? 'danger' : sectionTimeLeft < 300 ? 'warning' : ''}`}>
                            <Clock size={16} /> {formatTime(sectionTimeLeft)}
                        </div>
                    )}
                </div>

                {/* Progress bar */}
                <div style={{ width: '100%', height: 4, background: 'var(--color-bg-tertiary)', borderRadius: 2, marginBottom: 24, overflow: 'hidden' }}>
                    <div style={{ width: `${((currentQuestionIndex + 1) / sectionQuestions.length) * 100}%`, height: '100%', background: '#6366f1', transition: 'width 0.2s ease' }} />
                </div>

                {/* Question dots navigation */}
                <div className="flex gap-2 mb-6 justify-center" style={{ flexWrap: 'wrap' }}>
                    {sectionQuestions.map((q: any, idx: number) => (
                        <button key={idx} onClick={() => setCurrentQuestionIndex(idx)}
                            style={{
                                width: 32, height: 32, borderRadius: 8, border: '1px solid var(--color-border)',
                                background: idx === currentQuestionIndex ? '#6366f1' : answers[q.id] ? 'rgba(16, 185, 129, 0.15)' : 'var(--color-bg-card)',
                                color: idx === currentQuestionIndex ? 'white' : answers[q.id] ? '#10b981' : '#6b7280',
                                fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                                transition: 'all 150ms',
                            }}>
                            {idx + 1}
                        </button>
                    ))}
                </div>

                {/* Question Container Card */}
                {currentQ && (
                    <div className="card animate-fadeIn" style={{ maxWidth: 800, margin: '0 auto', padding: 32 }}>
                        <div className="flex items-center gap-3 mb-4">
                            <span className={`badge ${currentQ.type === 'mcq' ? 'badge-accent' : currentQ.type === 'speaking' ? 'badge-warning' : 'badge-info'}`}>
                                {currentQ.type === 'mcq' ? 'MCQ' : currentQ.type === 'speaking' ? 'Webcam Speaking' : 'Subjective'}
                            </span>
                        </div>

                        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 24, lineHeight: 1.6 }}>
                            {currentQ.prompt}
                        </h2>

                        {/* MCQ renderer */}
                        {currentQ.type === 'mcq' && (currentQ.config as any)?.options && (
                            <div className="flex-col gap-3" style={{ display: 'flex' }}>
                                {(currentQ.config as any).options.map((opt: any) => (
                                    <button key={opt.id}
                                        onClick={() => handleAnswer(currentQ.id, { selectedOptionIds: [opt.id] })}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 12,
                                            padding: '14px 16px', borderRadius: 10,
                                            background: (answers[currentQ.id]?.selectedOptionIds || []).includes(opt.id)
                                                ? 'rgba(99, 102, 241, 0.08)' : 'var(--color-bg-tertiary)',
                                            border: `2px solid ${(answers[currentQ.id]?.selectedOptionIds || []).includes(opt.id) ? '#6366f1' : 'transparent'}`,
                                            color: 'var(--color-text-primary)', cursor: 'pointer',
                                            transition: 'all 150ms', width: '100%', textAlign: 'left',
                                            fontSize: '14px',
                                        }}>
                                        <div style={{
                                            width: 20, height: 20, borderRadius: '50%',
                                            border: `2px solid ${(answers[currentQ.id]?.selectedOptionIds || []).includes(opt.id) ? '#6366f1' : '#6b7280'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {(answers[currentQ.id]?.selectedOptionIds || []).includes(opt.id) && (
                                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#6366f1' }} />
                                            )}
                                        </div>
                                        {opt.text}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Subjective/Essay renderer */}
                        {currentQ.type === 'subjective' && (
                            <textarea
                                className="form-textarea"
                                value={answers[currentQ.id]?.text || ''}
                                onChange={(e) => handleAnswer(currentQ.id, { text: e.target.value })}
                                placeholder="Type your response answer here..."
                                rows={8}
                                style={{ fontSize: '14px', lineHeight: 1.8 }}
                            />
                        )}

                        {/* Webcam speaking test renderer */}
                        {currentQ.type === 'speaking' && (
                            <WebcamRecorder
                                questionId={currentQ.id}
                                savedVideoKey={answers[currentQ.id]?.videoKey}
                                prompt={currentQ.prompt}
                                onSaved={(videoKey) => handleAnswer(currentQ.id, { videoKey })}
                                token={token!}
                            />
                        )}

                        {/* Question navigation footer */}
                        <div className="flex justify-between mt-8" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 20 }}>
                            <button className="btn btn-secondary" onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                                disabled={currentQuestionIndex === 0}>
                                <ChevronLeft size={16} /> Previous
                            </button>

                            {currentQuestionIndex === sectionQuestions.length - 1 ? (
                                <button className="btn btn-primary" onClick={() => {
                                    if (confirm('Finish this section? You will not be able to modify these responses.')) {
                                        submitSectionMutation.mutate({ sectionId: activeSectionId, attId: (attemptId || assessInfo?.activeAttempt?.attemptId)! });
                                    }
                                }} disabled={submitSectionMutation.isPending}>
                                    <Send size={16} /> {submitSectionMutation.isPending ? 'Submitting Section...' : 'Submit Section'}
                                </button>
                            ) : (
                                <button className="btn btn-primary" onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}>
                                    Next <ChevronRight size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Sections Dashboard (Multi-section dashboard)
    // Runs when started, but no section is active (e.g. at start or in-between sections)
    // Fetch attempt details again by start mutation query cache
    const activeAttemptState = assessInfo?.activeAttempt || startMutation.data?.data || {};
    const attemptSections = activeAttemptState.sections || {};

    return (
        <div style={{ minHeight: '100vh', background: 'var(--color-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div className="auth-card animate-slideUp" style={{ maxWidth: 640, width: '100%', padding: 32 }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{ fontSize: '3rem', marginBottom: 12 }}>📋</div>
                    <h2 style={{ fontWeight: 800, fontSize: '1.6rem' }}>Assessment Dashboard</h2>
                    <p style={{ color: '#6b7280', fontSize: '13px' }}>Please complete the sections below in sequential order</p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
                    {assessInfo?.sections?.map((sec: any, idx: number) => {
                        const secMeta = attemptSections[sec.id] || {};
                        const status = secMeta.status || 'pending'; // pending, started, completed
                        
                        // Section locking rule: unlocked if first, or if all previous completed
                        const prevSections = assessInfo.sections.slice(0, idx);
                        const isUnlocked = idx === 0 || prevSections.every((ps: any) => attemptSections[ps.id]?.status === 'completed');

                        return (
                            <div key={sec.id} style={{
                                padding: '16px 20px',
                                borderRadius: 12,
                                border: '1px solid var(--color-border)',
                                background: status === 'completed' ? 'rgba(16, 185, 129, 0.03)' : 'var(--color-bg-tertiary)',
                                opacity: isUnlocked ? 1 : 0.6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                transition: 'all 0.15s ease',
                            }}>
                                <div className="flex items-center gap-4">
                                    <div style={{
                                        width: 36, height: 36, borderRadius: '50%',
                                        background: status === 'completed' ? '#10b981' : isUnlocked ? '#6366f1' : 'var(--color-bg-primary)',
                                        color: status === 'completed' || isUnlocked ? 'white' : '#6b7280',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: '14px'
                                    }}>
                                        {status === 'completed' ? '✓' : idx + 1}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--color-text-primary)' }}>{sec.name}</div>
                                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                            {sec.questionCount} Questions · {sec.timeLimitMinutes ? `${sec.timeLimitMinutes} minutes` : 'No limit'}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    {status === 'completed' ? (
                                        <span className="badge badge-success" style={{ padding: '6px 12px', fontSize: '11px' }}>Completed</span>
                                    ) : !isUnlocked ? (
                                        <span className="badge badge-secondary" style={{ padding: '6px 12px', fontSize: '11px' }}>Locked</span>
                                    ) : (
                                        <button className="btn btn-primary btn-sm flex items-center gap-1"
                                            onClick={() => resumeSection(sec.id, attemptId || activeAttemptState.attemptId)}
                                            disabled={startSectionMutation.isPending}>
                                            <Play size={12} fill="white" /> {status === 'started' ? 'Resume Test' : 'Start Test'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div style={{ background: 'var(--color-bg-primary)', borderRadius: 10, padding: 12, border: '1px solid var(--color-border)', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                    All submitted section answers are auto-saved. Do not close this browser window.
                </div>
            </div>
        </div>
    );
}

// ── Premium Webcam Recorder Component ────────────────────────

interface WebcamRecorderProps {
    questionId: string;
    onSaved: (videoKey: string) => void;
    savedVideoKey?: string;
    prompt: string;
    token: string;
}

function WebcamRecorder({ questionId, onSaved, savedVideoKey, prompt, token }: WebcamRecorderProps) {
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
    const [recordingState, setRecordingState] = useState<'idle' | 'preparing' | 'recording' | 'saving' | 'done'>('idle');
    const [prepTimeLeft, setPrepTimeLeft] = useState(30);
    const [recTimeLeft, setRecTimeLeft] = useState(90);
    const [uploading, setUploading] = useState(false);
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);

    const videoPreviewRef = useRef<HTMLVideoElement>(null);
    const chunksRef = useRef<Blob[]>([]);
    const prepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const recIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const startWebcam = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            toast.error("Your browser or device does not support webcam recording. Please try using a modern browser like Chrome, Safari, or Firefox.");
            return;
        }

        try {
            const userStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            setStream(userStream);
            if (videoPreviewRef.current) {
                videoPreviewRef.current.srcObject = userStream;
            }
            setRecordingState('preparing');
            setPrepTimeLeft(30);

            prepIntervalRef.current = setInterval(() => {
                setPrepTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(prepIntervalRef.current!);
                        startRecording(userStream);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (err) {
            toast.error("Camera and Microphone permissions are required for the speaking test.");
        }
    };

    const startRecording = (activeStream: MediaStream) => {
        if (prepIntervalRef.current) clearInterval(prepIntervalRef.current);
        chunksRef.current = [];

        // Check for supported media type fallbacks
        let mimeType = 'video/webm;codecs=vp8,opus';
        if (typeof MediaRecorder.isTypeSupported === 'function') {
            if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
            if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/mp4;codecs=h264,aac';
            if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/mp4';
            if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = ''; // Let browser choose default
        } else {
            mimeType = ''; // Mobile Safari or older browsers fallback
        }

        try {
            const options = mimeType ? { mimeType } : undefined;
            const recorder = new MediaRecorder(activeStream, options);

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };

            recorder.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
                setRecordedBlob(blob);
                setRecordingState('saving');
                await uploadVideo(blob);
            };

            setMediaRecorder(recorder);
            recorder.start();
            setRecordingState('recording');
            setRecTimeLeft(90);

            recIntervalRef.current = setInterval(() => {
                setRecTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(recIntervalRef.current!);
                        recorder.stop();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (err: any) {
            console.error('[WebcamRecorder] MediaRecorder init failed:', err);
            toast.error('Failed to start recording on this device. Please check permissions or try a different browser.');
            setRecordingState('idle');
            if (stream) {
                stream.getTracks().forEach(t => t.stop());
                setStream(null);
            }
        }
    };

    const stopRecording = () => {
        if (recIntervalRef.current) clearInterval(recIntervalRef.current);
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }
        if (stream) {
            stream.getTracks().forEach(t => t.stop());
            setStream(null);
        }
    };

    const uploadVideo = async (blob: Blob) => {
        setUploading(true);
        try {
            const formData = new FormData();
            const extension = blob.type.includes('mp4') ? 'mp4' : 'webm';
            formData.append('video', blob, `${questionId}.${extension}`);

            const res = await fetch(`${API_BASE}/public/assessment/${token}/upload-video`, {
                method: 'POST',
                body: formData
            });
            if (!res.ok) throw new Error('Video upload failed');
            const data = await res.json();

            onSaved(data.data.videoKey);
            setRecordingState('done');
            setRecordedBlob(null);
            toast.success('Response video saved successfully!');
        } catch (err) {
            console.error('[WebcamRecorder] Upload failed:', err);
            toast.error('Failed to upload video. You can retry the upload or record again.');
            setRecordingState('saving'); // stay in saving state to show retry options
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (prepIntervalRef.current) clearInterval(prepIntervalRef.current);
            if (recIntervalRef.current) clearInterval(recIntervalRef.current);
            if (stream) stream.getTracks().forEach(t => t.stop());
        };
    }, [stream]);

    if (savedVideoKey || recordingState === 'done') {
        return (
            <div style={{ textAlign: 'center', padding: 24, background: 'rgba(16, 185, 129, 0.04)', borderRadius: 12, border: '1px solid rgba(16, 185, 129, 0.15)', marginTop: 16 }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✅</div>
                <h3 style={{ fontWeight: 700, marginBottom: 8, color: '#10b981' }}>Speaking Response Saved</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: 16 }}>Your video recording has been uploaded successfully.</p>
                {savedVideoKey && (
                    <video src={`${API_BASE}/uploads/${savedVideoKey}`} controls style={{ width: '100%', maxWidth: 360, borderRadius: 8, background: '#000', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                )}
                <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => {
                        setRecordingState('idle');
                        onSaved('');
                    }}>
                        Record Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', marginTop: 16, width: '100%' }}>
            {recordingState === 'idle' && (
                <div style={{ textAlign: 'center', padding: 32, background: 'var(--color-bg-tertiary)', borderRadius: 12, border: '1px solid var(--color-border)', width: '100%' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>🎥</div>
                    <h3 style={{ fontWeight: 700, marginBottom: 8 }}>Webcam English Speaking Test</h3>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: 24 }}>
                        You will have 30 seconds to read the question prompt, followed by up to 90 seconds to record your answer.
                    </p>
                    <button className="btn btn-primary" onClick={startWebcam}>
                        Allow Camera & Start Test
                    </button>
                </div>
            )}

            {(recordingState === 'preparing' || recordingState === 'recording' || recordingState === 'saving') && (
                <div style={{ position: 'relative', width: '100%', maxWidth: 480, aspectRatio: '4/3', borderRadius: 12, overflow: 'hidden', background: '#000', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
                    <video ref={videoPreviewRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
                    
                    {/* Preparing overlay */}
                    {recordingState === 'preparing' && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', padding: 24, textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>PREPARATION TIME</div>
                            <div style={{ fontSize: '3.5rem', fontWeight: 800, marginBottom: 16 }}>{prepTimeLeft}s</div>
                            <button className="btn btn-primary btn-sm" onClick={() => stream && startRecording(stream)}>
                                Start Recording Now
                            </button>
                        </div>
                    )}

                    {/* Recording indicator overlay */}
                    {recordingState === 'recording' && (
                        <div style={{ position: 'absolute', top: 16, left: 16, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.6)', padding: '6px 12px', borderRadius: 20, fontSize: '12px', color: 'white', fontWeight: 600 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                            <span>REC · {recTimeLeft}s</span>
                        </div>
                    )}

                    {/* Saving/Uploading/Retry overlay */}
                    {recordingState === 'saving' && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', padding: 24, textAlign: 'center' }}>
                            {uploading ? (
                                <>
                                    <div className="loading-spinner" style={{ marginBottom: 16 }} />
                                    <div style={{ fontSize: '13px', fontWeight: 600 }}>Saving & uploading speaking test video...</div>
                                </>
                            ) : (
                                <>
                                    <AlertTriangle size={36} color="#ef4444" style={{ marginBottom: 12 }} />
                                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: 16 }}>Upload failed due to connection issue</div>
                                    <div className="flex gap-2">
                                        <button className="btn btn-primary btn-sm" onClick={() => recordedBlob && uploadVideo(recordedBlob)}>
                                            Retry Upload
                                        </button>
                                        <button className="btn btn-secondary btn-sm" onClick={() => {
                                            setRecordingState('idle');
                                            setRecordedBlob(null);
                                        }}>
                                            Record Again
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {recordingState === 'recording' && (
                <button className="btn btn-primary" onClick={stopRecording} style={{ background: '#ef4444', borderColor: '#ef4444' }}>
                    Stop & Submit Video
                </button>
            )}
        </div>
    );
}
